import { useMemo } from 'react';

import type { DatePickerInputProps } from '../types';

/**
 * inputProps에 width가 없으면 기본 width를 자동으로 설정하는 커스텀 훅
 *
 * @description
 * - inputProps가 없으면 기본 width를 가진 객체 반환
 * - inputProps에 width가 이미 있으면 그대로 반환
 * - inputProps에 width가 없으면 기본 width 추가
 * - useMemo로 불필요한 재생성 방지
 *
 * @param inputProps - DatePickerInput에 전달할 props (선택적)
 * @param defaultWidth - 기본 width 값 (기본값: '352px')
 * @returns width가 설정된 inputProps
 *
 * @example
 * ```tsx
 * const enhancedInputProps = useInputPropsWithDefaultWidth(inputProps, '352px');
 * ```
 */
export const useInputPropsWithDefaultWidth = (
  inputProps?: DatePickerInputProps,
  defaultWidth = '352px',
): DatePickerInputProps => {
  return useMemo(() => {
    // inputProps가 없으면 기본 width만 반환
    if (!inputProps) {
      return { style: { width: defaultWidth } };
    }

    // width가 이미 있으면 그대로 반환
    const hasWidth = inputProps.style?.width !== undefined;
    if (hasWidth) {
      return inputProps;
    }

    // width가 없으면 기본 width 추가
    return {
      ...inputProps,
      style: {
        ...inputProps.style,
        width: defaultWidth,
      },
    };
  }, [inputProps, defaultWidth]);
};
